/**
 * Probate Risk Detector
 *
 * Identifies risks likely to delay, complicate, or increase the cost
 * of administering an estate. Sits on top of the IHT calculation engine
 * — does not duplicate tax logic.
 *
 * Input:  IHTCalculation + EstateAssets + EstateProfile
 * Output: ProbateRiskAssessment (overall level + ranked risks)
 */

import type {
    IHTCalculation, EstateAssets, EstateProfile,
    ProbateRisk, ProbateRiskAssessment, RiskSeverity,
} from '../../types';

// ── Helpers ──────────────────────────────────────────────────────────

const fmt = (n: number) => '£' + Math.round(n).toLocaleString('en-GB');

function daysSince(dateStr: string | undefined | null): number | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

function sumAssetValues(assets: EstateAssets | undefined, key: 'bank_accounts' | 'investments'): number {
    return (assets?.[key] ?? []).reduce((s, a) => {
        const val = Number((a as any).estimated_value) || 0;
        const isJoint = (a as any).ownership === 'joint';
        return s + (isJoint ? val / 2 : val);
    }, 0);
}

function liquidAssets(assets: EstateAssets | undefined): number {
    return sumAssetValues(assets, 'bank_accounts') + sumAssetValues(assets, 'investments');
}

function propertyTotal(assets: EstateAssets | undefined): number {
    // Net equity (value − mortgage) × deceased share, matching how the engine
    // builds gross_value (probateDerivations). Using the pre-mortgage value here
    // against a net-of-mortgage gross estate produced ratios above 100%.
    return (assets?.properties ?? []).reduce((s, p) => {
        const fullVal = Number(p.estimated_value) || 0;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        const netEquity = Math.max(0, fullVal - mortgage);
        const share = (p.deceased_share_percent ?? 100) / 100;
        return s + netEquity * share;
    }, 0);
}

// ── Risk detection rules ─────────────────────────────────────────────

type Detector = (
    iht: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
) => ProbateRisk | null;

// ── 1. Tax Payment ──────────────────────────────────────────────────

const taxPaymentDue: Detector = (iht) => {
    const tax = iht?.total_tax_due ?? 0;
    if (tax <= 0) return null;
    return {
        id: 'tax_payment_due',
        category: 'tax_payment',
        severity: tax > 100_000 ? 'high' : 'medium',
        name: 'Inheritance tax payment required',
        why: `Estimated inheritance tax due is ${fmt(tax)}. This must normally be paid before probate is granted for most of the estate.`,
        action: 'Confirm how the tax will be funded and whether instalment payments apply.',
        triggers: [`IHT due: ${fmt(tax)}`],
    };
};

const taxDeadlineApproaching: Detector = (iht, _a, profile) => {
    const tax = iht?.total_tax_due ?? 0;
    if (tax <= 0) return null;
    const days = daysSince(profile?.deceased?.date_of_death);
    if (days === null) return null;
    // IHT is due 6 months after end of month of death
    if (days > 150) {
        return {
            id: 'tax_deadline_near',
            category: 'tax_payment',
            severity: days > 180 ? 'high' : 'medium',
            name: days > 180 ? 'IHT payment may be overdue' : 'IHT payment deadline approaching',
            why: days > 180
                ? `It has been ${days} days since the date of death. IHT is normally due within 6 months of the end of the month of death. Interest accrues on late payments.`
                : `It has been ${days} days since the date of death. The IHT payment deadline is approaching.`,
            action: 'Submit the IHT400 and arrange payment to HMRC as soon as possible.',
            triggers: [`${days} days since death`, `IHT due: ${fmt(tax)}`],
        };
    }
    return null;
};

const cashShortfall: Detector = (iht, assets) => {
    const tax = iht?.total_tax_due ?? 0;
    if (tax <= 0) return null;
    const liquid = liquidAssets(assets);
    if (liquid >= tax) return null;
    const shortfall = tax - liquid;
    return {
        id: 'cash_shortfall',
        category: 'tax_payment',
        severity: shortfall > tax * 0.5 ? 'high' : 'medium',
        name: 'Liquid assets may not cover the tax due',
        why: `Currently identified liquid assets (${fmt(liquid)}) may not cover the estimated inheritance tax of ${fmt(tax)}. Potential shortfall: ${fmt(shortfall)}.`,
        action: 'Review the HMRC instalment option for property and business assets, or consider a bridging loan.',
        triggers: [`Liquid: ${fmt(liquid)}`, `Tax: ${fmt(tax)}`, `Shortfall: ${fmt(shortfall)}`],
    };
};

// ── 2. Probate Delay ────────────────────────────────────────────────

const fullIht400Required: Detector = (iht) => {
    const tax = iht?.total_tax_due ?? 0;
    const gross = iht?.breakdown?.gross_value ?? 0;
    if (tax > 0 || gross > 3_000_000) {
        return {
            id: 'iht400_required',
            category: 'probate_delay',
            severity: 'medium',
            name: 'Full IHT400 form required',
            why: 'The estate requires a full IHT400 return to HMRC. This involves detailed asset schedules and typically takes longer than the simpler IHT205/IHT217 route.',
            action: 'Gather valuations for all assets and complete the IHT400 with supporting schedules.',
            triggers: tax > 0 ? [`IHT due: ${fmt(tax)}`] : [`Gross estate: ${fmt(gross)}`],
        };
    }
    return null;
};

const missingValuations: Detector = (_iht, assets) => {
    const missing = (assets?.properties ?? []).filter(p => !p.estimated_value || p.estimated_value === 0);
    if (missing.length === 0) return null;
    return {
        id: 'missing_valuations',
        category: 'probate_delay',
        severity: 'medium',
        name: 'Property valuations missing',
        why: `${missing.length} propert${missing.length === 1 ? 'y has' : 'ies have'} no valuation. HMRC requires date-of-death valuations for all property assets.`,
        action: 'Obtain a professional valuation or RICS report for each property.',
        triggers: missing.map(p => p.address || 'Unnamed property'),
    };
};

const noWillConfirmed: Detector = (_iht, _a, profile) => {
    if (profile?.representatives?.will_exists === 'no') {
        return {
            id: 'no_will',
            category: 'probate_delay',
            severity: 'medium',
            name: 'No will — intestacy rules apply',
            why: 'Without a valid will, the estate is distributed under intestacy rules. This requires a grant of Letters of Administration rather than probate, which may take longer.',
            action: 'Confirm no will exists and apply for Letters of Administration.',
            triggers: ['Will: not found'],
        };
    }
    return null;
};

// ── 3. Relief Eligibility ───────────────────────────────────────────

// NOTE: the former `rnrbUnconfirmed` detector was removed. RNRB is now gated
// upstream in the engine — it is only applied against a confirmed close-
// inheritance link (descendant immediate interest / absolute / allocation).
// So `rnrb_available > 0` ⇒ the link is confirmed by construction, and this
// detector could only ever FALSE-fire "link not confirmed" alongside an applied
// RNRB — the exact contradiction this revision eliminates. The withheld-RNRB
// case (unconfirmed/discretionary link) is surfaced by the engine's own `rnrb`
// confidence flags. The single source of truth is breakdown.closely_inherited_flag.

const tnrbUnverified: Detector = (iht, _a, profile) => {
    const tnrbPct = iht?.breakdown?.reliefs?.tnrb?.transferred_percent ?? 0;
    if (tnrbPct <= 0) return null;
    const sp = profile?.iht_details?.predeceased_spouse;
    if (sp?.records_available === 'full') return null;
    return {
        id: 'tnrb_unverified',
        category: 'relief_eligibility',
        severity: sp?.records_available === 'none' ? 'high' : 'medium',
        name: 'Transferred NRB assumed but not fully verified',
        why: `A ${tnrbPct}% NRB transfer from a predeceased spouse is included in the calculation. HMRC requires documentary evidence from the first spouse's estate.`,
        action: 'Obtain the first spouse\'s death certificate, probate records, and IHT return.',
        triggers: [`TNRB: ${tnrbPct}%`, `Records: ${sp?.records_available ?? 'unknown'}`],
    };
};

const aprBprUnconfirmed: Detector = (iht, assets) => {
    const bprTotal = iht?.breakdown?.bpr_relief_total ?? 0;
    const aprTotal = iht?.breakdown?.apr_relief_total ?? 0;
    if (bprTotal + aprTotal <= 0) return null;
    const unconfirmedBpr = (assets?.bpr_assets ?? []).filter(a => a.qualifies !== true && (Number(a.estimated_value) || 0) > 0);
    const unconfirmedApr = (assets?.apr_assets ?? []).filter(a => a.qualifies !== true && (Number(a.estimated_value) || 0) > 0);
    if (unconfirmedBpr.length === 0 && unconfirmedApr.length === 0) return null;
    const labels = [...unconfirmedBpr.map(a => a.label), ...unconfirmedApr.map(a => a.label)].filter(Boolean);
    return {
        id: 'apr_bpr_evidence',
        category: 'relief_eligibility',
        severity: 'high',
        name: 'APR/BPR claimed without confirmed eligibility',
        why: `Business or agricultural relief is included but eligibility has not been confirmed for all assets. HMRC may challenge claims without supporting evidence.`,
        action: 'Gather ownership records, accounts, and valuation evidence for each qualifying asset.',
        triggers: labels.length > 0 ? labels : ['Unconfirmed qualifying assets'],
    };
};

const incomeExemptionNoRecords: Detector = (_iht, assets) => {
    const gifts = assets?.lifetime_gifts ?? [];
    const incomeGifts = gifts.filter(g => g.is_regular_from_income);
    if (incomeGifts.length === 0) return null;
    const hasIncomeData = assets?.income_assessment &&
        ((assets.income_assessment as any).annual_income > 0 || (assets.income_assessment as any).total_income > 0);
    if (hasIncomeData) return null;
    return {
        id: 'income_exemption_evidence',
        category: 'relief_eligibility',
        severity: 'medium',
        name: 'Normal expenditure exemption claimed without income records',
        why: 'Gifts have been marked as regular expenditure from income, but no income/expense data has been provided. HMRC requires evidence that gifts were made from surplus income.',
        action: 'Gather bank statements, tax returns, and income records to support the s21 exemption claim.',
        triggers: [`${incomeGifts.length} gift(s) claimed as regular income expenditure`],
    };
};

// ── 4. Liquidity ────────────────────────────────────────────────────

const propertyHeavyEstate: Detector = (iht, assets) => {
    const gross = iht?.breakdown?.gross_value ?? 0;
    if (gross <= 0) return null;
    const propVal = propertyTotal(assets);
    const ratio = propVal / gross;
    if (ratio < 0.6) return null;
    return {
        id: 'property_heavy',
        category: 'liquidity',
        severity: ratio > 0.8 ? 'high' : 'medium',
        name: 'Estate is property-heavy',
        why: `Property makes up ${Math.round(ratio * 100)}% of the gross estate (${fmt(propVal)} of ${fmt(gross)}). This may make it difficult to raise funds for tax, debts, and administration costs without selling property.`,
        action: 'Review the HMRC instalment option for property-related tax or consider early marketing of the property.',
        triggers: [`Property: ${fmt(propVal)}`, `Ratio: ${Math.round(ratio * 100)}%`],
    };
};

// ── 5. Ownership / Title ────────────────────────────────────────────

const tenantsInCommonIssue: Detector = (_iht, assets) => {
    const tic = (assets?.properties ?? []).filter(p => p.ownership_type === 'tenants_in_common');
    if (tic.length === 0) return null;
    return {
        id: 'tenants_in_common',
        category: 'ownership_title',
        severity: 'medium',
        name: 'Tenants in common property requires probate',
        why: `${tic.length} propert${tic.length === 1 ? 'y is' : 'ies are'} held as tenants in common. The deceased's share does not pass automatically and must be transferred through probate.`,
        action: 'Verify the deceased\'s share percentage and confirm title registration with the Land Registry.',
        triggers: tic.map(p => p.address || 'Unnamed property'),
    };
};

const unclearOwnership: Detector = (_iht, assets) => {
    const unclear = [
        ...(assets?.properties ?? []).filter(p => !p.ownership_type || p.ownership_type === 'unknown'),
        ...(assets?.bank_accounts ?? []).filter(a => !(a as any).ownership),
        ...(assets?.investments ?? []).filter(i => !(i as any).ownership),
    ];
    if (unclear.length === 0) return null;
    return {
        id: 'unclear_ownership',
        category: 'ownership_title',
        severity: unclear.length > 2 ? 'high' : 'medium',
        name: 'Ownership unclear on some assets',
        why: `${unclear.length} asset${unclear.length === 1 ? ' has' : 's have'} unknown ownership type. This affects which assets form part of the estate and how they are distributed.`,
        action: 'Check title deeds, account documentation, and any trust declarations to confirm ownership.',
        triggers: [`${unclear.length} asset(s) with unknown ownership`],
    };
};

// ── 6. Gift Review ──────────────────────────────────────────────────

const largeChargeableGifts: Detector = (iht) => {
    const chargeable = iht?.breakdown?.gifts_chargeable_total ?? 0;
    if (chargeable <= 0) return null;
    const nrb = iht?.breakdown?.reliefs?.tnrb?.combined_nrb ?? 325_000;
    const exceedsNrb = chargeable > nrb;
    return {
        id: 'large_gifts',
        category: 'gift_review',
        severity: exceedsNrb ? 'high' : chargeable > 50_000 ? 'medium' : 'low',
        name: exceedsNrb ? 'Chargeable gifts exceed the Nil Rate Band' : 'Significant chargeable gifts recorded',
        why: exceedsNrb
            ? `Chargeable gifts of ${fmt(chargeable)} exceed the available NRB (${fmt(nrb)}). The excess is subject to IHT, and the gifts also reduce the NRB available for the estate.`
            : `Chargeable gifts of ${fmt(chargeable)} have been recorded. These reduce the Nil Rate Band available for the estate.`,
        action: 'Verify gift dates, amounts, and recipients. Gather evidence for any exemptions claimed.',
        triggers: [`Chargeable gifts: ${fmt(chargeable)}`],
    };
};

const giftEvidenceIncomplete: Detector = (_iht, assets) => {
    const gifts = assets?.lifetime_gifts ?? [];
    if (gifts.length === 0) return null;
    const noDate = gifts.filter(g => !g.date);
    const noAmount = gifts.filter(g => !g.amount || g.amount <= 0);
    const incomplete = noDate.length + noAmount.length;
    if (incomplete === 0) return null;
    return {
        id: 'gift_evidence_incomplete',
        category: 'gift_review',
        severity: 'medium',
        name: 'Incomplete gift records',
        why: `${incomplete} gift record${incomplete === 1 ? ' is' : 's are'} missing dates or amounts. HMRC requires complete records for all gifts within 7 years of death.`,
        action: 'Review bank statements and financial records to complete gift dates and amounts.',
        triggers: noDate.length > 0 ? [`${noDate.length} without date`] : [`${noAmount.length} without amount`],
    };
};

// ── 7. Family / Beneficiary Conflict ────────────────────────────────

const unequalChildTreatment: Detector = (_iht, assets, profile) => {
    const childrenOver18 = profile?.family?.children_over_18 ?? 0;
    const childrenUnder18 = profile?.family?.children_under_18 ?? 0;
    const totalChildren = childrenOver18 + childrenUnder18;
    if (totalChildren < 2) return null;

    // Check for large gifts to a single recipient
    const gifts = assets?.lifetime_gifts ?? [];
    const otherGifts = gifts.filter(g => g.recipient_type === 'other');
    const totalGiftValue = otherGifts.reduce((s, g) => s + (g.amount || 0), 0);
    if (totalGiftValue < 50_000) return null;

    return {
        id: 'unequal_children',
        category: 'family_conflict',
        severity: totalGiftValue > 200_000 ? 'high' : 'medium',
        name: 'Large lifetime gifts may cause disputes',
        why: `${fmt(totalGiftValue)} in lifetime gifts has been recorded alongside ${totalChildren} children. Unequal lifetime giving can lead to family disputes during estate administration.`,
        action: 'Consider whether lifetime gifts should be brought into account when distributing the estate, and communicate with all beneficiaries early.',
        triggers: [`${totalChildren} children`, `Lifetime gifts: ${fmt(totalGiftValue)}`],
    };
};

const spouseChildSplit: Detector = (_iht, assets, profile) => {
    const hasSpouse = profile?.deceased?.maritalStatus === 'MARRIED_OR_CP';
    const hasChildren = ((profile?.family?.children_over_18 ?? 0) + (profile?.family?.children_under_18 ?? 0)) > 0;
    if (!hasSpouse || !hasChildren) return null;

    // Check if assets split between spouse and children
    const props = assets?.properties ?? [];
    const hasSplitProp = props.some(p => {
        const allocs = p.beneficiary_allocations ?? [];
        const hasSpouseAlloc = allocs.some(a => a.type === 'spouse');
        const hasChildAlloc = allocs.some(a => a.type === 'direct_descendant');
        return hasSpouseAlloc && hasChildAlloc;
    });

    if (!hasSplitProp) return null;
    return {
        id: 'spouse_child_split',
        category: 'family_conflict',
        severity: 'low',
        name: 'Property split between spouse and children',
        why: 'At least one property is divided between the surviving spouse and children. This can create practical difficulties, especially if the spouse wishes to continue living in the property.',
        action: 'Consider whether a trust or life interest arrangement is appropriate, and take legal advice if needed.',
        triggers: ['Property allocated to both spouse and children'],
    };
};

// ── Assemble all detectors ──────────────────────────────────────────

const ALL_DETECTORS: Detector[] = [
    // Tax payment
    taxPaymentDue,
    taxDeadlineApproaching,
    cashShortfall,
    // Probate delay
    fullIht400Required,
    missingValuations,
    noWillConfirmed,
    // Relief eligibility
    tnrbUnverified,
    aprBprUnconfirmed,
    incomeExemptionNoRecords,
    // Liquidity
    propertyHeavyEstate,
    // Ownership / title
    tenantsInCommonIssue,
    unclearOwnership,
    // Gift review
    largeChargeableGifts,
    giftEvidenceIncomplete,
    // Family / beneficiary conflict
    unequalChildTreatment,
    spouseChildSplit,
];

// ── Severity scoring ────────────────────────────────────────────────

const SEVERITY_WEIGHT: Record<RiskSeverity, number> = { low: 1, medium: 2, high: 3 };
const SEVERITY_ORDER: RiskSeverity[] = ['high', 'medium', 'low'];

function deriveOverallLevel(risks: ProbateRisk[]): RiskSeverity {
    if (risks.length === 0) return 'low';
    const totalWeight = risks.reduce((s, r) => s + SEVERITY_WEIGHT[r.severity], 0);
    const hasHigh = risks.some(r => r.severity === 'high');
    if (hasHigh || totalWeight >= 8) return 'high';
    if (totalWeight >= 4) return 'medium';
    return 'low';
}

// ── Public API ──────────────────────────────────────────────────────

export function assessProbateRisks(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
): ProbateRiskAssessment {
    const risks: ProbateRisk[] = [];

    for (const detect of ALL_DETECTORS) {
        const risk = detect(ihtCalc, assets, profile);
        if (risk) risks.push(risk);
    }

    // Sort by severity (high first), then by category
    risks.sort((a, b) => {
        const sevDiff = SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
        if (sevDiff !== 0) return sevDiff;
        return a.category.localeCompare(b.category);
    });

    return {
        overall_risk_level: deriveOverallLevel(risks),
        risks,
    };
}
